import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Typography } from '@material-ui/core';
import VehicleIcon from '@material-ui/icons/DirectionsCar';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSDatePicker,
} from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import SearchPlaceAutoComplete from '../../../shared/components/modules/SearchPlaceAutoComplete';
import SearchVehicleAutoComplete from '../../../shared/components/modules/SearchVehicleAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import SectionLabel from './SectionLabel';
import personVehicleRegistrationSchema from '../../schema/personVehicleRegistrationSchema';
import vehicleRegistrationSchema from '../../schema/vehicleRegistrationSchema';
import { DEFAULT_VALUES } from '../../constants/personVehicleRegistrationFormConstants';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import {
  createVehicleRegistration,
  updateVehicleRegistration,
} from '../../services/vehicleService';
import { searchPlaceAddresses, getAuthorityAddressById } from '../../services/addressService';
import PartyKindLookup from './PartyKindLookup';
import { useEditVehicleDialog } from 'features/user/hooks/useEditVehicleDialog';
import { PARTY_KIND } from '../../constants/partyKindLookupConstants';

const VehicleRegistrationDialog = ({
  open,
  onClose,
  personId,
  parentId,
  partyType = PARTY_KIND.PERSON,
  canSave = true,
  registration = null,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const vehiclePerms = usePermissions(PERMISSION_RESOURCES.VEHICLE);
  const canViewVehicleRecord = Boolean(vehiclePerms.Read || vehiclePerms.Edit);
  const [submitting, setSubmitting] = useState(false);
  const [authorityAddressOptions, setAuthorityAddressOptions] = useState([]);
  const [, setLoadingAuthorityAddress] = useState(false);
  const { openEditVehicleDialog } = useEditVehicleDialog();
  const isEditMode = Boolean(registration?.ptsRegistrationID);

  const methods = useForm({
    resolver: CreateFormResolver(
      partyType === PARTY_KIND.VEHICLE ? vehicleRegistrationSchema : personVehicleRegistrationSchema
    ),
    disabled: !canSave,
    mode: 'onBlur',
    defaultValues: { ...DEFAULT_VALUES, ...(registration || {}) },
  });

  const { control, setValue } = methods;
  const { isDirty } = useFormState({ control });
  const partyId = useWatch({ control, name: 'ptsParentID' });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const {
    stateOptions,
    plateTypeOptions,
    plateColorOptions,
    plateStatusOptions,
    plateDetailOptions,
  } = useUserDictionaryOptions();

  const watched = useWatch({
    control,
    name: [
      'ptsVehicleID',
      'Make',
      'Model',
      'Year',
      'VIN',
      'Style',
      'Color',
      'ptsPlateIssuingAuthorityID',
      'ptsLesseeID',
      'ptsLessorID',
    ],
  });
  const [
    ptsVehicleID,
    Make,
    Model,
    Year,
    VIN,
    Style,
    Color,
    ptsPlateIssuingAuthorityID,
    ptsLesseeID,
    ptsLessorID,
  ] = watched;

  const loadInitialAuthorityAddress = useCallback(() => {
    if (!isEditMode || !registration?.ptsAddressID) return;

    executeApiAction({
      setLoading: setLoadingAuthorityAddress,
      dispatch,
      action: () => getAuthorityAddressById(registration.ptsAddressID),
      onSuccess: (address) => {
        if (address) setAuthorityAddressOptions((prev) => [address, ...prev]);
      },
    });
  }, [isEditMode, registration, dispatch]);

  useEffect(() => {
    if (!ptsPlateIssuingAuthorityID) {
      setAuthorityAddressOptions([]);
      return;
    }

    executeApiAction({
      setLoading: setLoadingAuthorityAddress,
      dispatch,
      action: () => searchPlaceAddresses(ptsPlateIssuingAuthorityID),
      onSuccess: (addresses) => setAuthorityAddressOptions(addresses),
    });
  }, [ptsPlateIssuingAuthorityID, dispatch]);

  useEffect(() => {
    loadInitialAuthorityAddress();
  }, [loadInitialAuthorityAddress]);

  const handleVehicleSelected = useCallback(
    (vehicle) => {
      if (!vehicle) return;
      setValue('ptsVehicleID', vehicle.ptsVehicleID, { shouldValidate: true });
      setValue('Make', vehicle.Make || '');
      setValue('Model', vehicle.Model || '');
      setValue('Year', vehicle.Year || '');
      setValue('VIN', vehicle.VIN || '');
      setValue('Style', vehicle.Style || '');
      setValue('Color', vehicle.PrimaryColor || '');
      setValue('PlateNumber', vehicle.PlateNumber || '');
    },
    [setValue]
  );

  const handleVehicleUpdated = useCallback(
    (updatedVehicle) => {
      if (!updatedVehicle) return;
      setValue('Make', updatedVehicle.Make || '');
      setValue('Model', updatedVehicle.Model || '');
      setValue('Year', updatedVehicle.Year || '');
      setValue('VIN', updatedVehicle.VIN || '');
      setValue('Style', updatedVehicle.Style || '');
      setValue('Color', updatedVehicle.PrimaryColor || '');
    },
    [setValue]
  );

  const handleViewVehicle = useCallback(() => {
    openEditVehicleDialog(ptsVehicleID, { onVehicleUpdated: handleVehicleUpdated });
  }, [openEditVehicleDialog, ptsVehicleID, handleVehicleUpdated]);

  const handleVehicleCreated = useCallback(
    ({ vehicle, registration: newRegistration } = {}) => {
      if (!vehicle) return;

      setValue('ptsVehicleID', vehicle.ptsVehicleID, { shouldValidate: true });
      setValue('Make', vehicle.Make || '');
      setValue('Model', vehicle.Model || '');
      setValue('Year', vehicle.Year || '');
      setValue('VIN', vehicle.VIN || '');
      setValue('Style', vehicle.Style || '');
      setValue('Color', vehicle.PrimaryColor || '');
      setValue('PlateNumber', newRegistration?.PlateNumber || '');
      setValue('PlateState', newRegistration?.PlateState || '');
    },
    [setValue]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsParentID: partyType === PARTY_KIND.VEHICLE ? data.ptsParentID : parentId ?? personId,
        PartyType: partyType === PARTY_KIND.VEHICLE ? data.PartyType : partyType,
        ptsVehicleID: data.ptsVehicleID,
        Make: data.Make || null,
        Model: data.Model || null,
        Year: data.Year || null,
        VIN: data.VIN || null,
        Style: data.Style || null,
        Color: data.Color || null,
        PlateNumber: data.PlateNumber || null,
        PlateState: data.PlateState || null,
        PlateType: data.PlateType || null,
        PlateExpiration: data.PlateExpiration || null,
        ptsPlateIssuingAuthorityID: data.ptsPlateIssuingAuthorityID || null,
        ptsAddressID: data.ptsAddressID || null,
        PlateColor: data.PlateColor || null,
        PlateBackgroundText: data.PlateBackgroundText || null,
        PlateStatus: data.PlateStatus || null,
        DecalExpiration: data.DecalExpiration || null,
        PlateDetail: data.PlateDetail || null,
        TitleText: data.TitleText || null,
        InsuranceCompany: data.InsuranceCompany || null,
        VehicleValue: data.VehicleValue || null,
        OdometerReading: data.OdometerReading || null,
        GrossWeightMeasure: data.GrossWeightMeasure || null,
        isPermanentReg: data.isPermanentReg || false,
        RoadIndicator: data.RoadIndicator || false,
        IsLeased: data.IsLeased || false,
        LesseeType: data.LesseeType || null,
        ptsLesseeID: data.ptsLesseeID || null,
        LessorType: data.LessorType || null,
        ptsLessorID: data.ptsLessorID || null,
      };

      if (isEditMode) {
        payload.UpdatedBy = currentUser?.Username;
      } else {
        payload.CreatedBy = currentUser?.Username;
        payload.UpdatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? updateVehicleRegistration(registration.ptsRegistrationID, payload)
            : createVehicleRegistration(payload),
        onSuccess: (savedRegistration) => {
          dispatch(
            notify(
              isEditMode
                ? 'Vehicle registration updated successfully'
                : 'Vehicle registration created successfully',
              'success'
            )
          );
          onSaved?.(savedRegistration);
        },
      });
    },
    [isEditMode, registration, personId, parentId, partyType, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this vehicle registration. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={ptsVehicleID ? 'Vehicle Registration' : 'Add Vehicle Registration'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting || !canSave}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!registration?.Created}
            created={registration?.Created}
            createdBy={registration?.CreatedBy}
            updated={registration?.Updated}
            updatedBy={registration?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        {partyType === PARTY_KIND.VEHICLE && (
          <Box className="rms-dialog-section">
            <SectionLabel>Party</SectionLabel>
            <Grid container spacing={2}>
              <PartyKindLookup
                typeFieldName="PartyType"
                idFieldName="ptsParentID"
                typeLabel="Type"
                idLabel="Party Search"
                idValue={partyId}
              />
            </Grid>
          </Box>
        )}
        {!isEditMode && !ptsVehicleID && (
          <Box className="rms-dialog-section">
            <SectionLabel>Vehicle Lookup</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SearchVehicleAutoComplete
                  name="ptsVehicleIDSearch"
                  label="Search Vehicle"
                  onSelectionChange={handleVehicleSelected}
                  showAddIcon
                  onVehicleCreated={handleVehicleCreated}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {ptsVehicleID && (
          <>
            {partyType !== PARTY_KIND.VEHICLE && (
              <Box className="rms-dialog-section">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <SectionLabel>Vehicle Details</SectionLabel>
                  {canViewVehicleRecord && (
                    <RMSIconButton
                      icon={<VehicleIcon fontSize="small" />}
                      tooltip="View Vehicle"
                      onClick={handleViewVehicle}
                    />
                  )}
                </Box>
                <Typography variant="body2">
                  <strong>Make:</strong> {Make || '-'} &nbsp; <strong>Model:</strong> {Model || '-'}
                  &nbsp; <strong>Year:</strong> {Year || '-'} &nbsp; <strong>VIN:</strong>{' '}
                  {VIN || '-'}
                  &nbsp; <strong>Style:</strong> {Style || '-'} &nbsp; <strong>Color:</strong>{' '}
                  {Color || '-'}
                </Typography>
              </Box>
            )}

            <Box className="rms-dialog-section">
              <SectionLabel>Plate</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <RMSTextField
                    name="PlateNumber"
                    label="Plate Number"
                    inputProps={{ maxLength: 25 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="PlateState"
                    label="Plate State"
                    options={stateOptions}
                    {...getDictionarySelectProps()}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="PlateType"
                    label="Plate Type"
                    options={plateTypeOptions}
                    {...getDictionarySelectProps()}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSDatePicker name="PlateExpiration" label="Plate Expiration Date" />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="PlateColor"
                    label="Plate Color"
                    options={plateColorOptions}
                    {...getDictionarySelectProps()}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="PlateStatus"
                    label="Plate Status"
                    options={plateStatusOptions}
                    {...getDictionarySelectProps()}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="PlateDetail"
                    label="Plate Detail"
                    options={plateDetailOptions}
                    {...getDictionarySelectProps()}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSDatePicker name="DecalExpiration" label="Decal Expiration" />
                </Grid>
                <Grid item xs={12}>
                  <RMSTextField
                    name="PlateBackgroundText"
                    label="Plate Background Text"
                    inputProps={{ maxLength: 2000 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SearchPlaceAutoComplete
                    name="ptsPlateIssuingAuthorityID"
                    label="Plate Issuing Authority"
                    defaultPlaceId={ptsPlateIssuingAuthorityID}
                    showAddIcon={false}
                    showDetailsIcon={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <RMSSelect
                    name="ptsAddressID"
                    label="Issuing Authority Address"
                    options={authorityAddressOptions}
                    optionValue="ptsAddressID"
                    optionLabel="FullAddressText"
                    showLabelOnly
                    disabled={!ptsPlateIssuingAuthorityID}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box className="rms-dialog-section rms-dialog-section--last">
              <SectionLabel>Registration</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <RMSTextField
                    name="TitleText"
                    label="Title Text"
                    inputProps={{ maxLength: 2000 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField
                    name="InsuranceCompany"
                    label="Insurance Company"
                    inputProps={{ maxLength: 300 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField
                    name="VehicleValue"
                    label="Vehicle Value"
                    type="number"
                    inputProps={{ min: 0, max: 100000000000 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField
                    name="OdometerReading"
                    label="Odometer Reading"
                    type="number"
                    inputProps={{ min: -900000000, max: 900000000 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField
                    name="GrossWeightMeasure"
                    label="Gross Weight Measure"
                    type="number"
                    inputProps={{ min: -90, max: 90 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSCheckbox name="isPermanentReg" label="Permanent Registration" />
                </Grid>
                <Grid item xs={4}>
                  <RMSCheckbox name="RoadIndicator" label="Road Indicator" />
                </Grid>
                <Grid item xs={4}>
                  <RMSCheckbox name="IsLeased" label="Leased" />
                </Grid>

                <PartyKindLookup
                  typeFieldName="LesseeType"
                  idFieldName="ptsLesseeID"
                  typeLabel="Lessee"
                  idLabel="Lessee Search"
                  idValue={ptsLesseeID}
                />
                <PartyKindLookup
                  typeFieldName="LessorType"
                  idFieldName="ptsLessorID"
                  typeLabel="Lessor"
                  idLabel="Lessor Search"
                  idValue={ptsLessorID}
                />
              </Grid>
            </Box>
          </>
        )}
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default VehicleRegistrationDialog;
